import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, BookOpen, ClipboardList, NotebookPen, Printer, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Shell } from "@/components/Shell";
import { getSections, visibleQuestions, formatAnswer, toReportPayload } from "@/data/questionnaire";
import { businessForAi } from "@/data/business";
import { computeStrategy, strategyToText } from "@/data/strategy";
import { staffToText } from "@/data/staff";
import { StrategyPanel } from "@/components/StrategyPanel";
import { WeightsEditor } from "@/components/WeightsEditor";
import { StaffComparison } from "@/components/StaffComparison";
import { EmailButton } from "@/components/EmailButton";
import { HandoffButton } from "@/components/HandoffButton";
import { marketingBlocks } from "@/lib/handoff";
import { AREAS } from "@/data/strategy";
import { getSubmission, upsertSubmission, formatDate, loadWeights, saveWeights, removeStaff } from "@/lib/storage";
import { streamReport } from "@/lib/reportStream";

export default function ResultPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(() => getSubmission(id));
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const started = useRef(false);
  const [weights, setWeights] = useState(loadWeights);
  const [weightsOpen, setWeightsOpen] = useState(false);
  const updateWeights = (w) => {
    saveWeights(w);
    setWeights(w);
  };
  const [notesSaved, setNotesSaved] = useState(false);
  const savedTimer = useRef(null);

  const saveNotes = (notes) => {
    setSub((s) => upsertSubmission({ ...s, notes }));
    setNotesSaved(true);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setNotesSaved(false), 1500);
  };

  const generate = () => {
    if (!sub) return;
    setError("");
    setStreaming(true);
    let text = "";
    setSub((s) => ({ ...s, report: "", reportDone: false }));
    streamReport({ ...toReportPayload(sub, strategyToText(computeStrategy(sub.answers, weights, sub.businessType))), staff: staffToText(sub), business: businessForAi() }, {
      onToken: (t) => {
        text += t;
        setSub((s) => ({ ...s, report: text }));
      },
      onDone: () => {
        setStreaming(false);
        setSub((s) => upsertSubmission({ ...s, report: text, reportDone: true }));
      },
      onError: (e) => {
        setStreaming(false);
        setError(e.message || "Errore durante la generazione");
        toast.error("Report non generato. Controlla la connessione e riprova.");
      },
    });
  };

  useEffect(() => {
    if (!sub) {
      navigate("/", { replace: true });
      return;
    }
    if (!sub.reportDone && !started.current) {
      started.current = true;
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!sub) return null;
  const strategy = computeStrategy(sub.answers, weights, sub.businessType);
  const sections = getSections(sub.businessType);
  const reportDoc = () => ({
    kind: "report", title: sub.respondent, subtitle: `${sub.businessName || ""} · ${formatDate(sub.completedAt || sub.createdAt)}`,
    blocks: [
      { heading: `Strategia: ${strategy.label}`, paragraphs: strategy.pace ? [strategy.pace] : [] },
      ...["primary", "secondary", "tertiary"].map((k, i) => ({ heading: ["Azioni primarie", "Azioni secondarie", "Azioni terziarie"][i], bullets: strategy[k].map((a) => `${a.title} (${AREAS[a.area]}) — ${a.reasons.join("; ")}`) })),
      { heading: "Report AI", markdown: sub.report || "(non generato)" },
      ...sections.map((s) => ({ heading: `Risposte · ${s.title}`, rows: visibleQuestions(s, sub.answers).map((q) => [q.text, formatAnswer(q, sub.answers[q.id]) || "Non risposto"]) })),
      ...(sub.notes ? [{ heading: "Note del consulente", paragraphs: [sub.notes] }] : []),
    ],
  });
  return (
    <Shell wide header={<span className="text-sm text-[#7A6B63]">{formatDate(sub.completedAt || sub.createdAt)}</span>}>
      <div className="fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Risultato per il consulente</p>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-[#2C221E] mt-2" data-testid="result-respondent">{sub.respondent}</h1>
          </div>
          <div className="flex flex-wrap gap-3 no-print">
            <button className="btn btn-ghost" onClick={() => navigate("/")} data-testid="back-to-list-btn"><ArrowLeft size={18} /> Elenco</button>
            <button className="btn btn-ghost" onClick={generate} disabled={streaming} data-testid="regenerate-report-btn"><RefreshCw size={18} /> Rigenera</button>
            <button className="btn btn-ghost" onClick={() => navigate(`/piano/${id}`)} data-testid="open-plan-btn"><ClipboardList size={18} /> Piano per i titolari</button>
            <button className="btn btn-ghost" onClick={() => navigate(`/metodo?da=${id}`)} data-testid="open-method-btn"><BookOpen size={18} /> Costruisci il metodo</button>
            <EmailButton build={reportDoc} />
            <HandoffButton chooser build={(focus = "tutto") => ({ ...reportDoc(), blocks: focus === "tutto" ? [...marketingBlocks(sub, strategy, sections), ...reportDoc().blocks] : marketingBlocks(sub, strategy, sections, focus) })} />
            <button className="btn btn-olive" onClick={() => window.print()} data-testid="print-report-btn"><Printer size={18} /> Stampa / PDF</button>
          </div>
        </div>

        <div className="mt-10 grid gap-6">
          <StrategyPanel strategy={strategy} weightsOpen={weightsOpen} onToggleWeights={() => setWeightsOpen((o) => !o)} />
          {weightsOpen && <WeightsEditor weights={weights} onChange={updateWeights} />}
          <StaffComparison sub={sub} onAdd={() => navigate(`/staff/${id}/nuovo`)} onResume={(p) => navigate(`/staff/${id}/${p.id}`)}
            onRemove={(p) => { if (window.confirm(`Eliminare le risposte di ${p.name}?`)) { removeStaff(id, p.id); setSub(getSubmission(id)); } }} />
        </div>

        <div className="grid lg:grid-cols-5 gap-8 mt-8">
          <section className="lg:col-span-3 card p-6 sm:p-9" data-testid="ai-report">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="text-[#D99B26]" size={22} />
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#382E2B]">Report AI</h2>
              {streaming && <span className="text-sm text-[#7A6B63]" data-testid="report-streaming-label">in scrittura…</span>}
            </div>
            {error && (
              <div className="rounded-xl border border-[#A83232]/30 bg-[#A83232]/5 p-4 text-[#A83232] mb-4" data-testid="report-error">{error}</div>
            )}
            {!sub.report && streaming && <p className="text-[#7A6B63] cursor-blink">Sto leggendo le risposte</p>}
            <div className={`report ${streaming ? "cursor-blink" : ""}`} data-testid="report-content">
              <ReactMarkdown>{sub.report}</ReactMarkdown>
            </div>
          </section>

          <aside className="lg:col-span-2 grid gap-5 content-start print-break" data-testid="answers-panel">
            <section className="card p-5 sm:p-6 border-[#D99B26]/50 bg-[#FFFBF2]" data-testid="consultant-notes">
              <div className="flex items-center gap-2 mb-1">
                <NotebookPen className="text-[#D99B26]" size={20} />
                <h2 className="font-serif text-2xl font-semibold text-[#382E2B]">Note del consulente</h2>
              </div>
              <p className="text-sm text-[#7A6B63] mb-3 no-print">Impressioni a caldo, visibili solo qui. Si salvano da sole.</p>
              <textarea className="field min-h-[140px] resize-y bg-white/70 print:border-0 print:p-0" rows={5}
                placeholder="Es. Maria molto decisa sulla cucina, Giovanni frena sul budget…"
                value={sub.notes || ""} onChange={(e) => saveNotes(e.target.value)} data-testid="consultant-notes-input" />
              <p className="text-xs text-[#B0A096] mt-2 h-4 no-print" data-testid="notes-saved-label">{notesSaved ? "Salvato" : ""}</p>
            </section>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#382E2B]">Le risposte</h2>
            {sections.map((s) => (
              <div key={s.id} className="card p-5 sm:p-6" data-testid={`result-section-${s.id}`}>
                <h3 className="font-serif text-xl font-semibold text-[#2D4030] mb-3">{s.title}</h3>
                <dl className="grid gap-3">
                  {visibleQuestions(s, sub.answers).map((q) => {
                    const a = formatAnswer(q, sub.answers[q.id]);
                    return (
                      <div key={q.id}>
                        <dt className="text-sm text-[#7A6B63]">{q.text}</dt>
                        <dd className={`mt-0.5 ${a ? "text-[#2C221E]" : "italic text-[#B0A096]"}`}>{a || "Non risposto"}</dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            ))}
          </aside>
        </div>
      </div>
    </Shell>
  );
}
